import { useEffect, useRef } from "react";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { sharedGet } from "@/lib/http";
import { useT } from "@/i18n/I18nContext";

const POLL_MS = 15000; // 15s
const LS_KEY = "solano_last_sp_seen";

// Fire a burst of confetti tuned for the "SP gained" moment (subtle).
function burstConfetti(strong = false) {
  const count = strong ? 200 : 90;
  const defaults = { origin: { y: 0.75 }, colors: ["#FC9700", "#FFD37E", "#518688", "#7AA37F", "#FFFFFF"] };
  confetti({ ...defaults, particleCount: count * 0.4, spread: 55, startVelocity: 45 });
  confetti({ ...defaults, particleCount: count * 0.3, spread: 100, decay: 0.92, scalar: 0.9 });
  confetti({ ...defaults, particleCount: count * 0.3, spread: 130, decay: 0.94, scalar: 1.2 });
}

const ACTION_LABEL_KEY = {
  meal_log: "rewards.actions.meal_log",
  meal_photo_bonus: "rewards.actions.meal_photo_bonus",
  plate_balanced: "rewards.actions.plate_balanced",
  water_goal: "rewards.actions.water_goal",
  steps_goal: "rewards.actions.steps_goal",
  workout_done: "rewards.actions.workout_done",
  sleep_7h: "rewards.actions.sleep_7h",
  learn_pill: "rewards.actions.learn_pill",
  diary_complete: "rewards.actions.diary_complete",
  streak_bonus: "rewards.actions.streak_bonus",
  daily_bonus: "rewards.actions.daily_bonus",
  weekly_bonus: "rewards.actions.weekly_bonus",
};

const ACTION_EMOJI = {
  meal_log: "🥗", meal_photo_bonus: "📸", plate_balanced: "🍽️",
  water_goal: "💧", steps_goal: "🚶", workout_done: "💪",
  sleep_7h: "😴", learn_pill: "📚", diary_complete: "📝",
  streak_bonus: "🔥", daily_bonus: "🎁", weekly_bonus: "🏆",
};

export default function SpNotifier() {
  const { t } = useT();
  const lastSpRef = useRef(null);
  const lastLevelRef = useRef(null);
  const lastEventKeyRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    let timer;

    const poll = async () => {
      try {
        const { data } = await sharedGet("/client/gamification/summary");
        if (cancelled || !data) return;

        const prevSp = lastSpRef.current;
        const prevLevel = lastLevelRef.current;
        const curSp = data.total_sp;
        const curLevel = data.level.current.idx;
        const latest = data.recent && data.recent[0];

        if (prevSp === null || prevSp === undefined) {
          // First load — seed baselines quietly.
          try {
            const stored = parseInt(localStorage.getItem(LS_KEY) || "", 10);
            if (Number.isFinite(stored) && stored !== curSp && curSp > stored) {
              // Missed gains while app was closed — show combined toast.
              const delta = curSp - stored;
              toast.success(`+${delta} ${t("rewards.spGained")}`, { duration: 4000, icon: "✨" });
              burstConfetti();
            }
          } catch {}
          lastSpRef.current = curSp;
          lastLevelRef.current = curLevel;
          lastEventKeyRef.current = latest?.key || null;
          try { localStorage.setItem(LS_KEY, String(curSp)); } catch {}
          return;
        }

        // Level up detection — bigger burst + banner
        if (curLevel > prevLevel) {
          const name = t(`rewards.states.${data.level.current.key}.name`);
          toast(
            `${data.level.current.emoji} ${t("rewards.levelUpToast").replace("{name}", name)}`,
            { duration: 5000 }
          );
          burstConfetti(true);
        }

        // SP delta — single subtle toast attributed to latest event when possible
        if (curSp > prevSp) {
          const delta = curSp - prevSp;
          const key = latest?.key;
          if (key && key !== lastEventKeyRef.current) {
            const action = latest.action;
            const emoji = ACTION_EMOJI[action] || "✨";
            const label = t(ACTION_LABEL_KEY[action] || `rewards.actions.${action}`);
            toast.success(`${emoji} +${delta} SP · ${label}`, { duration: 3200 });
          } else {
            toast.success(`✨ +${delta} ${t("rewards.spGained")}`, { duration: 3000 });
          }
          burstConfetti();
        }

        lastSpRef.current = curSp;
        lastLevelRef.current = curLevel;
        lastEventKeyRef.current = latest?.key || null;
        try { localStorage.setItem(LS_KEY, String(curSp)); } catch {}
      } catch {
        // Silent on 401/network — will retry.
      }
    };

    // Expose imperative refresh
    window.solanoSpCheck = poll;
    poll();
    timer = setInterval(poll, POLL_MS);
    return () => { cancelled = true; clearInterval(timer); delete window.solanoSpCheck; };
  }, [t]);

  return null;
}
